import React, { useCallback, useEffect, useState } from "react";
import { Input } from "../../ui/Input";
import { RadioGroup } from "../../ui/RadioGroup";
import { Select } from "../../ui/Select";
import { SubHead } from "../../ui/SubHead";
import { SubHeadWithButton } from "../../ui/subHeadWithButton";
import { SelectCountry } from "../../selectors/country/selectCountry";
import { yesNoOption } from "../../options/options";
import { v4 as uuidv4 } from "uuid";

const buildBlankTrustee = () => ({
    id: uuidv4(),
    name: "",
    registeredOffice: "",
    companyNumber: "",
});

const buildBlankNominee = () => ({
    id: uuidv4(),
    name: "",
    country: "",
    companyNumber: "",
    registeredOffice: "",
});

const pillButtonStyle = {
    border: "none",
    cursor: "pointer",
    color: "#fff",
    padding: "4px 14px",
    borderRadius: "6px",
    fontSize: "16px",
    lineHeight: 1.2,
    minWidth: "32px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
};

const addPillStyle = { ...pillButtonStyle, background: "#07b1b0", marginLeft: "10px" };
const removePillStyle = { ...pillButtonStyle, background: "#d9534f" };

export const UnitTrustForm = React.memo(({ formData, onChange, isReadOnly, isReport }) => {
    const [trustees, setTrustees] = useState(formData.uboTrustees || []);
    const [nominees, setNominees] = useState(formData.uboNominees || []);

    // Seed one nominee on mount.
    useEffect(() => {
        if (!Array.isArray(nominees) || nominees.length === 0) {
            setNominees([buildBlankNominee()]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Sync nominees up to parent formData.
    useEffect(() => {
        if (JSON.stringify(nominees) !== JSON.stringify(formData.uboNominees)) {
            onChange({ target: { name: "uboNominees", value: nominees } });
        }
    }, [nominees, formData.uboNominees, onChange]);

    // Seed one trustee on mount.
    useEffect(() => {
        if (!Array.isArray(trustees) || trustees.length === 0) {
            setTrustees([buildBlankTrustee()]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Sync trustees up to parent formData.
    useEffect(() => {
        if (JSON.stringify(trustees) !== JSON.stringify(formData.uboTrustees)) {
            onChange({ target: { name: "uboTrustees", value: trustees } });
        }
    }, [trustees, formData.uboTrustees, onChange]);

    const addNominee = useCallback(() => {
        setNominees((prev) => [...(prev || []), buildBlankNominee()]);
    }, []);

    const updateNominee = useCallback((id, field, value) => {
        setNominees((prev) => prev.map((n) => (n.id === id ? { ...n, [field]: value } : n)));
    }, []);

    const removeNominee = useCallback((id) => {
        setNominees((prev) => prev.filter((n) => n.id !== id));
    }, []);

    const addTrustee = useCallback((e) => {
        e.preventDefault();
        setTrustees((prev) => [...(prev || []), buildBlankTrustee()]);
    }, []);

    const updateTrustee = useCallback((id, field, value) => {
        setTrustees((prev) => prev.map((t) => (t.id === id ? { ...t, [field]: value } : t)));
    }, []);

    const removeTrustee = useCallback((id) => {
        setTrustees((prev) => prev.filter((t) => t.id !== id));
    }, []);

    const unitHolders = Array.isArray(formData.uboUnitHolderNames) && formData.uboUnitHolderNames.length > 0
        ? formData.uboUnitHolderNames
        : [""];

    const updateUnitHolder = (index, newValue) => {
        const next = [...unitHolders];
        next[index] = newValue;
        onChange({ target: { name: "uboUnitHolderNames", value: next } });
    };

    const addUnitHolder = () => {
        onChange({ target: { name: "uboUnitHolderNames", value: [...unitHolders, ""] } });
    };

    const removeUnitHolder = (index) => {
        const next = unitHolders.filter((_, i) => i !== index);
        onChange({ target: { name: "uboUnitHolderNames", value: next } });
    };

    return (
        <>
            <SubHead text={"Basic Information"} isReport={isReport} />

            <Input
                label={"Name of Unit Trust"}
                name={"uboUnitTrustName"}
                disabled={isReadOnly}
                value={formData.uboUnitTrustName}
                onChange={onChange}
                isReport={isReport}
                required
            />

            <Input
                label={"Name of Settlor"}
                name={"uboSettlorName"}
                disabled={isReadOnly}
                value={formData.uboSettlorName}
                onChange={onChange}
                isReport={isReport}
                required
            />

            <Input
                label={"Purpose of Trust"}
                name={"uboTrustPurpose"}
                disabled={isReadOnly}
                value={formData.uboTrustPurpose}
                onChange={onChange}
                isReport={isReport}
                required
            />

            <Select
                label={"Channel Island State"}
                name={"uboChannelIslandState"}
                disabled={isReadOnly}
                value={formData.uboChannelIslandState}
                onChange={onChange}
                isReport={isReport}
                required
                options={[
                    { value: "Guernsey", display_value: "Guernsey" },
                    { value: "Jersey", display_value: "Jersey" },
                ]}
            />

            <Input
                label={"Name of Protector or Controller"}
                name={"uboProtectorOrControllerName"}
                disabled={isReadOnly}
                value={formData.uboProtectorOrControllerName}
                onChange={onChange}
                isReport={isReport}
                required
            />

            <SelectCountry
                label={"Country of Establishment"}
                name={"uboCountryOfEstablishment"}
                disabled={isReadOnly}
                value={formData.uboCountryOfEstablishment}
                onChange={onChange}
                isReport={isReport}
                required
            />

            <Input
                label={"Name of Unit Trust Manager/ Trust Company"}
                name={"uboUnitTrustManager"}
                disabled={isReadOnly}
                value={formData.uboUnitTrustManager}
                onChange={onChange}
                isReport={isReport}
                required
            />

            <SubHead text={"Nominee Companies Registered at Land Registry"} isReport={isReport} />

            {nominees.map((nominee, idx) => (
                <div key={nominee.id} style={{ marginBottom: "16px" }}>
                    {idx > 0 && !isReadOnly && (
                        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "6px" }}>
                            <button
                                type="button"
                                onClick={() => removeNominee(nominee.id)}
                                title="Remove this Nominee Company"
                                style={removePillStyle}
                            >
                                −
                            </button>
                        </div>
                    )}

                    <Input
                        label={
                            idx === 0 ? (
                                <span
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        width: "100%",
                                    }}
                                >
                                    <span>
                                        Name of Nominee Company who holds the legal title to the Property at the Land Registry*
                                    </span>
                                    <button
                                        type="button"
                                        onClick={addNominee}
                                        disabled={isReadOnly}
                                        title="Add another Nominee Company"
                                        style={addPillStyle}
                                    >
                                        +
                                    </button>
                                </span>
                            ) : (
                                "Name of Nominee Company who holds the legal title to the Property at the Land Registry"
                            )
                        }
                        name={`uboNomineeName_${nominee.id}`}
                        value={nominee.name}
                        onChange={(e) => updateNominee(nominee.id, "name", e.target.value)}
                        disabled={isReadOnly}
                        isReport={isReport}
                        required={false}
                    />

                    <SelectCountry
                        label={"Country of Establishment"}
                        name={`uboNomineeCountry_${nominee.id}`}
                        value={nominee.country}
                        onChange={(e) => updateNominee(nominee.id, "country", e.target.value)}
                        disabled={isReadOnly}
                        isReport={isReport}
                        required={idx === 0}
                    />

                    <Input
                        label={"Company Number"}
                        name={`uboNomineeCompanyNumber_${nominee.id}`}
                        value={nominee.companyNumber}
                        onChange={(e) => updateNominee(nominee.id, "companyNumber", e.target.value)}
                        disabled={isReadOnly}
                        isReport={isReport}
                        required={idx === 0}
                    />

                    <Input
                        label={"Registered Office"}
                        name={`uboNomineeRegisteredOffice_${nominee.id}`}
                        value={nominee.registeredOffice}
                        onChange={(e) => updateNominee(nominee.id, "registeredOffice", e.target.value)}
                        disabled={isReadOnly}
                        isReport={isReport}
                        required={idx === 0}
                    />
                </div>
            ))}

            <SubHeadWithButton
                heading={"Trustee(s) of the Unit Trust"}
                buttons={[
                    {
                        buttonText: "+",
                        onClick: (e) => {
                            if (!isReadOnly) addTrustee(e);
                        },
                        style: { backgroundColor: isReadOnly ? "#ccc" : "white" },
                    },
                ]}
                isReport={isReport}
            />
            {trustees.map((trustee, idx) => (
                <div
                    key={trustee.id}
                    style={{ padding: "10px", border: "1px solid #ccc", borderRadius: "5px", marginBottom: "10px" }}
                >
                    <SubHeadWithButton
                        heading={`Trustee ${idx + 1}`}
                        buttons={[
                            {
                                buttonText: "x",
                                onClick: () => {
                                    if (!isReadOnly) removeTrustee(trustee.id);
                                },
                                style: {
                                    backgroundColor: isReadOnly ? "#ccc" : "red",
                                    color: "white",
                                },
                            },
                        ]}
                        isReport={isReport}
                    />
                    <Input
                        label={"Name"}
                        name={`uboTrusteeName_${trustee.id}`}
                        value={trustee.name}
                        onChange={(e) => updateTrustee(trustee.id, "name", e.target.value)}
                        disabled={isReadOnly}
                        isReport={isReport}
                        required
                    />
                    <Input
                        label={"Registered Office"}
                        name={`uboTrusteeRegisteredOffice_${trustee.id}`}
                        value={trustee.registeredOffice}
                        onChange={(e) => updateTrustee(trustee.id, "registeredOffice", e.target.value)}
                        disabled={isReadOnly}
                        isReport={isReport}
                        required
                    />
                    <Input
                        label={"Company Number"}
                        name={`uboTrusteeCompanyNumber_${trustee.id}`}
                        value={trustee.companyNumber}
                        onChange={(e) => updateTrustee(trustee.id, "companyNumber", e.target.value)}
                        disabled={isReadOnly}
                        isReport={isReport}
                        required
                    />
                </div>
            ))}

            <SubHead text={"Ultimate Beneficial Owners"} isReport={isReport} />

            <RadioGroup
                label={"Is there a +25% Unit Holder?"}
                name={"uboHasUnitHolder"}
                value={formData.uboHasUnitHolder}
                onChange={onChange}
                disabled={isReadOnly}
                isReport={isReport}
                options={yesNoOption}
                required
            />

            {formData.uboHasUnitHolder === "Yes" && unitHolders.map((value, index) => (
                <Input
                    type="text"
                    key={index}
                    name={`uboUnitHolderNames[${index}]`}
                    value={value}
                    onChange={(e) => updateUnitHolder(index, e.target.value)}
                    disabled={isReadOnly}
                    isReport={isReport}
                    required={false}
                    label={
                        <span
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%",
                            }}
                        >
                            <span>
                                Identify +25% Unit Holders
                                {index === 0 && "*"}
                            </span>
                            {index === 0 ? (
                                <button
                                    type="button"
                                    onClick={addUnitHolder}
                                    disabled={isReadOnly}
                                    title="Add another"
                                    style={{ ...addPillStyle, marginLeft: 0 }}
                                >
                                    +
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => removeUnitHolder(index)}
                                    disabled={isReadOnly}
                                    title="Remove this entry"
                                    style={removePillStyle}
                                >
                                    −
                                </button>
                            )}
                        </span>
                    }
                />
            ))}
        </>
    );
});
